import { default_maximum_interval } from "ts-fsrs";

import type { ReviewMode } from "./modes.js";
import { type FeedbackSounds, defaultReviewConfig } from "./review-config.js";

/**
 * What a settings form may offer, and what it may set each setting to.
 *
 * `review-config.ts` says what a configuration is and what a reader gets
 * before they have chosen. This says what they may choose it to be, which is a
 * narrower thing and belongs to the form rather than to the scheduler.
 *
 * **These are the form's bounds and not the algorithm's.** FSRS holds any
 * retention above zero and up to one, and any interval ceiling at all. A
 * reader moving a slider is not an optimiser, and a number typed into a box by
 * somebody finding out what it does should not be able to produce a schedule
 * nobody could review under. So the choices are enumerated where enumerating
 * them reads better than a box, and bounded where a box is the right control.
 *
 * The bounds are read twice, by the markup that draws the controls and by
 * `./chosen-config.ts` that reads what comes back. A `min` on an input is a
 * courtesy to whoever is typing and nothing more, since a posted form is
 * whatever the sender chose to send.
 */

/**
 * The questions a reader may ask for, which is what the site can put in front
 * of them today.
 *
 * The same list a reader gets before they have chosen anything, deliberately.
 * `hanzi_to_english` wants a list of acceptable answers rather than one string
 * and has no grader, so offering it would be a card the session could not
 * draw. It arrives here on the day it arrives in `defaultReviewConfig.modes`,
 * which is what keeps the two lists from drifting apart.
 *
 * **`cloze_to_handwriting` stood outside that rule for one release.** It was
 * offered on the form without being a default, on the argument that a reader
 * who had not asked for a card needing a finger should not be given one. It is
 * a default now, so the two lists were one again.
 *
 * **`english_to_speech` stands outside it now, on the same argument.** A card
 * answered aloud needs a microphone and costs a BytePlus call per attempt, so
 * a reader turns it on here and nobody is given it unasked. See docs/voice.md.
 */
export const offeredModes: readonly ReviewMode[] = [
  ...defaultReviewConfig.modes,
  "english_to_speech",
];

/**
 * The retentions a reader may choose between.
 *
 * A short list rather than a number, because the useful range is narrow and
 * the arithmetic behind it is not something to hand somebody a text box for.
 * 0.9 is FSRS's own default and sits in the middle. The two below it buy fewer
 * reviews with more forgetting and the one above buys the reverse, which is
 * the whole of what the setting does.
 */
export const retentionChoices: readonly number[] = [0.8, 0.85, 0.9, 0.95];

/**
 * What the sound setting may be set to, which is on or off.
 *
 * Enumerated for the same reason the retentions are. `./chosen-config.ts`
 * reads an absent field as the setting the reader already had, and a clear
 * checkbox posts nothing at all. One would be a switch that ticks and never
 * clears. Two radios post either way.
 */
export const feedbackSoundChoices: readonly FeedbackSounds[] = ["on", "off"];

/**
 * The sound setting one word asks for, where the word is one of the two.
 *
 * Read by the posted form and by the stored item, which is why it is here
 * rather than in either. Neither of them can trust what it holds: a form is
 * whatever the sender chose to send, and an item may have been written before
 * the setting existed.
 *
 * @param given - The word, as a form posted it or an item carries it.
 * @returns The setting, or nothing where the word is neither answer.
 */
export function offeredSound(
  given: string | null | undefined,
): FeedbackSounds | undefined {
  return feedbackSoundChoices.find((choice) => choice === given);
}

/** The hours a reader's day may be set to begin at. */
export const dayStartHours: readonly number[] = Array.from(
  { length: 24 },
  (_, hour) => hour,
);

/** A setting a form takes as a number, and the range it will take. */
export interface NumericBound {
  /** The smallest a form may set it to. */
  readonly least: number;

  /** The largest a form may set it to. */
  readonly most: number;
}

/**
 * The daily limits and the interval ceiling, as the ranges a form takes.
 *
 * **No limit may be set to zero.** A day allowing no cards would read as a
 * reader who had turned reviews off, and there is no such state here.
 * `defaultReviewConfig` says the same thing about its own generous defaults.
 *
 * The ceiling stops at FSRS's own default, which is a hundred years. Nothing
 * above it means anything to somebody learning a language.
 */
export const numericBounds: Readonly<
  Record<"dailyNewLimit" | "dailyReviewLimit" | "maximumInterval", NumericBound>
> = {
  dailyNewLimit: { least: 1, most: 200 },
  dailyReviewLimit: { least: 1, most: 2000 },
  maximumInterval: { least: 1, most: default_maximum_interval },
};
